import type { ReactElement } from "react";
import { Box } from "@mui/material";

import { dayjs } from "@thalamos/common";
import type {
  AuditTrailEntry,
  ExtendedThalamosUser,
  FormContextData,
} from "@thalamos/aspire-common";
import { AuditTrailEntryType } from "@thalamos/aspire-common";

import { getNextColor, myTeamColor } from "./helpers.js";
import { DateSeparator, TeamSeparator } from "./Separator.js";
import { ukLocalFormatDate, ukLocalFormatDateTime } from "~/util.js";

const GROUP_WINDOW_MS = 3_000;

/**
 * Collapse consecutive interviewParticipantAdded entries (search flow only)
 * that share the same interviewId and whose timestamps all fall within
 * GROUP_WINDOW_MS of the first entry in the group into a single synthetic
 * entry whose message lists all doctor names.
 */
export function groupSearchInvites(
  auditData: AuditTrailEntry[],
): AuditTrailEntry[] {
  const result: AuditTrailEntry[] = [];

  for (let i = 0; i < auditData.length; i++) {
    const entry = auditData[i];
    if (entry === undefined) continue;

    const isSearchInvite =
      entry.type === AuditTrailEntryType.interviewParticipantAdded &&
      entry.data?.inviteSource === "search";

    if (!isSearchInvite) {
      result.push(entry);
      continue;
    }

    const interviewId = entry.resources.find(
      (r) => r.resourceType === "interview",
    )?.resourceId;

    const group: AuditTrailEntry[] = [entry];

    while (i + 1 < auditData.length) {
      const next = auditData[i + 1];
      if (next === undefined) break;
      const nextIsSearchInvite =
        next.type === AuditTrailEntryType.interviewParticipantAdded &&
        next.data?.inviteSource === "search";
      const nextInterviewId = next.resources.find(
        (r) => r.resourceType === "interview",
      )?.resourceId;
      const withinWindow =
        dayjs(next.timestamp).diff(dayjs(entry.timestamp)) <= GROUP_WINDOW_MS;

      if (
        nextIsSearchInvite &&
        nextInterviewId === interviewId &&
        withinWindow
      ) {
        group.push(next);
        i++;
      } else {
        break;
      }
    }

    if (group.length === 1) {
      result.push(entry);
      continue;
    }

    const names = group
      .map(
        (e) => e.resources.find((r) => r.resourceType === "user")?.resourceName,
      )
      .filter((n): n is string => !!n);

    const syntheticEntry: AuditTrailEntry = {
      ...entry,
      data: {
        ...entry.data,
        _groupedDoctorNames: names,
      },
    };

    result.push(syntheticEntry);
  }

  return result;
}

export function entries(
  user: ExtendedThalamosUser,
  rawAuditData: AuditTrailEntry[],
  getMessage: (
    audit: AuditTrailEntry,
    formContextData?: FormContextData,
  ) => string | string[] | null,
  formContextData?: FormContextData,
): ReactElement[] {
  const sortedEntries = [...rawAuditData].sort((a, b) => {
    return dayjs(a.timestamp).isBefore(dayjs(b.timestamp)) ? -1 : 1;
  });

  const auditData = groupSearchInvites(
    sortedEntries.filter((auditEntry) =>
      getMessage(auditEntry, formContextData),
    ),
  );

  const elements: ReactElement[] = [];

  const firstEntry = auditData[0];
  // Should be impossible
  if (firstEntry === undefined) return [];

  let initialDate = ukLocalFormatDate(firstEntry.timestamp);
  let initialTeam = firstEntry.teamName;
  const myTeamId = user.sessionContext!.teamId;

  elements.push(<DateSeparator date={initialDate} />);
  elements.push(
    <TeamSeparator
      team={initialTeam!}
      myTeam={firstEntry.teamId === myTeamId}
    />,
  );

  const participantColours = new Map<string, string>();

  participantColours.set(myTeamId, myTeamColor);

  for (const auditEntry of auditData) {
    const message = getMessage(auditEntry, formContextData);

    if (message) {
      const newTimestamp = ukLocalFormatDate(auditEntry.timestamp);
      const newTeam = auditEntry.teamName;

      if (newTimestamp !== initialDate) {
        elements.push(<DateSeparator date={newTimestamp} />);
        initialDate = newTimestamp;
      }

      const myTeam = myTeamId === auditEntry.teamId;

      if (newTeam !== initialTeam) {
        elements.push(<TeamSeparator team={newTeam!} myTeam={myTeam} />);
        initialTeam = newTeam;
      }

      if (!participantColours.has(auditEntry.teamId!)) {
        // The map was seeded with the viewer's own team, which takes no palette
        // colour, so the other teams already coloured is one fewer.
        participantColours.set(
          auditEntry.teamId!,
          getNextColor(participantColours.size - 1),
        );
      }

      elements.push(
        <Box
          sx={{
            backgroundColor: participantColours.get(auditEntry.teamId!),
            display: "grid",
            mb: "0.5em",
            borderRadius: "0.5em",
            padding: "0.5em",
            paddingBottom: "0.7em",
            fontSize: "smaller",
            width: "80%",
            alignSelf: myTeam ? "flex-end" : "flex-start",
          }}
        >
          <Box sx={{ paddingBottom: 1 }}>
            {typeof message === "string"
              ? message
              : message.map((s) => (
                  <>
                    {s}
                    <br />
                  </>
                ))}
          </Box>
          <Box sx={{ justifySelf: "flex-end", fontSize: "7pt", mb: -1 }}>
            {auditEntry.userName
              ? `${auditEntry.organisationName === "Thalamos" ? "Thalamos Admin" : auditEntry.userName}: `
              : ""}
            {ukLocalFormatDateTime(auditEntry.timestamp)}
          </Box>
        </Box>,
      );
    }
  }

  return elements;
}
